import { Product } from '@/types/product'
import { Domain } from '@/types/dashboard/domain'
import { z } from 'zod'

export const Subscription = z.object({
  id: z.number().int(),
  started: z
    .number()
    .int()
    .min(-9_223_372_036_854_775_808)
    // eslint-disable-next-line no-loss-of-precision
    .max(9_223_372_036_854_775_807),
  active_until: z
    .number()
    .int()
    .min(-9_223_372_036_854_775_808)
    // eslint-disable-next-line no-loss-of-precision
    .max(9_223_372_036_854_775_807),
  status: z.enum(['active', 'canceled', 'suspended', 'superseded']),
  billing_freq: z.enum(['monthly', 'quarterly', 'half-annually', 'annually', 'free']),
  canceled: z
    .number()
    .int()
    .min(-9_223_372_036_854_775_808)
    // eslint-disable-next-line no-loss-of-precision
    .max(9_223_372_036_854_775_807)
    .optional(),
  product: Product,
  domain: Domain.optional(),
  used_features: z.record(z.string(), z.any()).optional(),
  // NIS2 suspension fields
  suspended_at: z.number().int().optional(),
  suspension_reason: z.string().optional(),
  // Withdrawal (Widerrufsrecht) fields
  withdrawn_at: z.number().int().optional(),
  withdrawable_until: z.number().int().optional(),
  // When a withdrawal declaration reached us, set before or without
  // withdrawn_at, since an operator settles one the panel cannot carry out.
  withdrawal_declared_at: z.number().int().optional(),
  // When a cancelled subscription's contract ends: the end of the billing
  // month of a monthly plan, the notice date of one paid for several months.
  access_until: z.number().int().optional(),
  // Refund of the unused full months of a prepaid plan: estimated while it is
  // cancelled and running, then what was refunded and when.
  estimated_refund_cents: z.number().int().optional(),
  prepaid_refund_cents: z.number().int().optional(),
  prepaid_refunded_at: z.number().int().optional()
})

export type Subscription = z.infer<typeof Subscription>

// What cancelling a subscription now would do.
export const CancelPreview = z.object({
  effective_at: z.number().int(),
  prepaid_until: z.number().int().optional(),
  estimated_refund_cents: z.number().int().optional()
})

export type CancelPreview = z.infer<typeof CancelPreview>

// What withdrawing a subscription now would refund, gross as charged. Absent
// when the backend could not work it out.
export const WithdrawPreview = z.object({
  refund_cents: z.number().int().optional()
})

export type WithdrawPreview = z.infer<typeof WithdrawPreview>
